import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import {
  BorderGridContainer,
  ErrorSummary,
  GridBox,
  H1,
  Hidden,
  Image,
  P,
  scrollToFirstError,
  SpacerWrapper,
} from '@ahmdigital/ui';
import { connect, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { INCOME_TYPE } from '@ahmdigital/constants';
import { isEmpty, pick } from 'lodash/fp';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import styled from 'styled-components';

import { Customer } from '../../selectors/select-customer/types';
import { Offer, Placeholder } from '../../components/offers/types';
import { ProductTypeType } from '../../types/product';
import { RootState } from '../../reducers';
import { SCALE_OPTIONS } from '../../models/customer/constants';
import constants from '../../ahm-constants';
import CustomerActions, { CustomerActionType } from '../../actions/customer';
import getDestinationUrl, { RefinePriceProductType } from './utils/get-destination-url';
import getRefinePriceQueryValues from '../../utils/get-refine-price-query-values';
import OfferBanner from '../../components/offers/banner';
import paperPlaneImage from '../../images/paper-plane.svg';
import RefinePriceForm from './form';
import refinePriceSchema, { RefinePriceSchemaType } from './form/schema';
import selectCustomer from '../../selectors/select-customer';
import selectCustomerFormInitialValues from '../../components/pricing-drawer/selectors/select-customer-form-initial-values';
import selectReferralCode from '../../selectors/select-referral-code';
import selectTargetSegment from '../../selectors/select-target-segment';
import submitRefinePrice from './utils/submit-refine-price';
import submitRefinePriceAnalytics from './utils/submit-refine-price-analytics';
import submitRefinePriceLead from './utils/submit-refine-price-lead';

const SCROLL_OFFSET = 100;

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;
  z-index: 1;

  @media (min-width: ${({ theme }) => theme.media.xs}) {
    border: 1px dotted ${({ theme }) => theme.colors.black};
    margin: 0 auto;
    max-width: 450px;
    padding: 2rem;
  }
`;

const StyledImage = styled(Image)`
  position: absolute;
  top: 375px;
  left: 50%;
  width: 700px;
  transform: translateX(-50%);

  @media (min-width: ${({ theme }) => theme.media.lg}) {
    width: 1100px;
  }
`;

const StyledErrorWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;
  z-index: 1;

  @media (min-width: ${({ theme }) => theme.media.xs}) {
    margin: 0 auto;
    max-width: 450px;
  }
`;

export type RefinePricePageProps = {
  actions: CustomerActionType;
  content: {
    activeOffers?: Offer[];
    placeholders?: Placeholder[];
  };
  customer: Customer;
  initialValues: RefinePriceSchemaType;
  targetSegment?: string;
};

const schemaForErrorSummary = {
  age: {
    linkPhrases: ['age'],
  },
  partnerAge: {
    linkPhrases: ['partner’s age'],
  },
  productType: {
    linkPhrases: ['health cover type'],
  },
};
const errorOrder = ['productType', 'age', 'partnerAge'];

type AllowedScale = RefinePriceSchemaType['scale'];

const isAllowedScaleOption = (value?: string): value is AllowedScale => SCALE_OPTIONS.includes(value as AllowedScale);

const RefinePricePage = ({ actions, content, customer, initialValues, targetSegment }: RefinePricePageProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [formikErrors, setFormikErrors] = useState({});
  const [formikTouched, setFormikTouched] = useState({});
  const [productTypeForOffers, setProductTypeForOffers] = useState<ProductTypeType | undefined>(
    initialValues?.productType as ProductTypeType | undefined,
  );
  const [scaleQuery, setScaleQuery] = useState<{
    incomeType: RefinePriceSchemaType['incomeType'] | '';
    scale: AllowedScale | '';
  }>({ incomeType: '', scale: '' });
  const referralCode: string | null = useSelector(selectReferralCode);

  useEffect(() => {
    if (!router.isReady) return;

    const params = new URLSearchParams(searchParams);
    const { scale: scaleParam } = getRefinePriceQueryValues({ params });

    if (isAllowedScaleOption(scaleParam)) {
      setScaleQuery({
        incomeType: scaleParam === INCOME_TYPE.SINGLE ? INCOME_TYPE.SINGLE : INCOME_TYPE.COUPLE,
        scale: scaleParam,
      });
    }
  }, [router, router.isReady, searchParams]);

  return (
    <BorderGridContainer hideBorderTop>
      <OfferBanner
        filterProductType={referralCode ? undefined : productTypeForOffers}
        overrideOffers={referralCode ? undefined : content?.activeOffers}
        placeholders={content?.placeholders}
        targetSegment={targetSegment}
      />
      <GridBox padding="1rem">
        <SpacerWrapper variant={['marginBottomSpacer4', 'marginTopSpacer9']}>
          <Hidden xs sm>
            <StyledImage src={paperPlaneImage.src} role="presentation" />
          </Hidden>
          {!isEmpty(formikErrors) && (
            <StyledErrorWrapper>
              <ErrorSummary
                schema={schemaForErrorSummary}
                errors={pick(errorOrder, formikErrors)}
                touched={formikTouched}
              />
            </StyledErrorWrapper>
          )}
          <Wrapper>
            <H1 variant="marginBottomSpacer10" looksLike="h3">
              Let’s find your cover
            </H1>
            <P variant="marginBottomSpacer8">Answer a few quick questions to tailor your health insurance results.</P>
            <Formik<RefinePriceSchemaType>
              enableReinitialize
              validationSchema={refinePriceSchema}
              initialValues={
                !scaleQuery.scale
                  ? initialValues
                  : { ...initialValues, incomeType: scaleQuery.incomeType, scale: scaleQuery.scale }
              }
              onSubmit={(values: RefinePriceSchemaType) => {
                submitRefinePrice({ actions, customer, formValues: values, initialValues });
                submitRefinePriceAnalytics(values);
                submitRefinePriceLead({
                  formValues: values,
                  leadSourceType: constants.LEAD_SOURCE_TYPES.REFINE_PRICE,
                });
                router.push(getDestinationUrl(values.productType as RefinePriceProductType));
              }}
            >
              {({ isSubmitting, errors, submitCount }) => {
                if (isSubmitting && !isEmpty(errors)) {
                  scrollToFirstError({ offset: SCROLL_OFFSET });
                }
                return (
                  <RefinePriceForm
                    captureFormState={{ setFormikErrors, setFormikTouched }}
                    handleChangeProductType={setProductTypeForOffers}
                    hasSubmitAttempt={Boolean(submitCount)}
                  />
                );
              }}
            </Formik>
          </Wrapper>
        </SpacerWrapper>
      </GridBox>
    </BorderGridContainer>
  );
};

const mapStateToProps = (state: RootState) => ({
  customer: selectCustomer(state),
  initialValues: selectCustomerFormInitialValues(state),
  targetSegment: selectTargetSegment(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
  },
});

// @ts-expect-error - Automatic, Please fix when editing this file
export default connect(mapStateToProps, mapDispatchToProps)(RefinePricePage);

export { RefinePricePage as RefinePricePageForTest };
